/**
 * QUIPWA — the canonical Markdown frontmatter parser/stringifier.
 *
 * There were three of these, one per layer, all parsing the same YAML-lite
 * dialect with quietly different rules — so the same file could be read
 * differently depending on which door it came through. This is the only one;
 * the notes model (note-model.ts), the sync and the importer all delegate here.
 *
 * Hand-rolled rather than a YAML library: gray-matter (the previous choice)
 * reaches for Node's `Buffer` and throws in the browser, which is the only
 * place this app runs. The dialect is the part of YAML that Obsidian's
 * Properties and people typing by hand actually write: `key: value`, where
 * keys may carry accents and spaces and values may be quoted, and lists either
 * as `[a, b]` or one `- item` per line. Whatever else a file holds (a nested
 * map, a `|` block, a blank value) is not modelled but kept verbatim, so a note
 * can go QUIPWA → Obsidian → QUIPWA without losing a key.
 */

const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/;
/** A wikilink as a value. Obsidian quotes these; a bare one still means the link, not a list. */
const WIKILINK_RE = /^\[\[[^[\]]*\]\]$/;
/** Blank lines and comments: they belong to no key. */
const NOTHING_RE = /^\s*(?:#.*)?$/;

export interface ParsedFrontmatter {
  frontmatter: Record<string, unknown>;
  body: string;
}

/**
 * A value kept exactly as the file wrote it: `text` is everything after the
 * key's colon, indented lines included. The parser keeps this way what the
 * dialect does not model (a nested map, a `|` block, a `{…}` map, a list of
 * lists) and blank values (`key:`, `key: ""`, `key: []` — the empty properties
 * of an Obsidian template), so stringify writes them back untouched. It reads
 * as absent: `readString` gives undefined and `readList` an empty list.
 */
export class RawYaml {
  readonly text: string;
  constructor(text: string) {
    this.text = text;
  }
}

/** YAML's double-quote escapes (a superset of JSON's). */
const ESCAPES: Record<string, string> = {
  '0': '\0', a: '\x07', b: '\b', t: '\t', '\t': '\t', n: '\n', v: '\v', f: '\f', r: '\r', e: '\x1b',
  ' ': ' ', '"': '"', '/': '/', '\\': '\\', N: '\x85', _: '\xa0', L: ' ', P: ' ',
};
const HEX_ESCAPES: Record<string, number> = { x: 2, u: 4, U: 8 };

/** A quoted scalar opening at `start`: its value and the index past its closing quote, or null if it never closes. */
function readQuoted(text: string, start: number): { value: string; end: number } | null {
  const quote = text[start];
  let value = '';
  for (let i = start + 1; i < text.length; i++) {
    const ch = text[i]!;
    if (ch === quote) {
      // Inside single quotes, a doubled quote is a literal one.
      if (quote === "'" && text[i + 1] === "'") {
        value += "'";
        i++;
        continue;
      }
      return { value, end: i + 1 };
    }
    if (ch === '\\' && quote === '"' && i + 1 < text.length) {
      const code = text[++i]!;
      const digits = HEX_ESCAPES[code] ?? 0;
      const hex = text.slice(i + 1, i + 1 + digits);
      const point = digits && hex.length === digits && /^[0-9a-f]+$/i.test(hex) ? parseInt(hex, 16) : NaN;
      if (point <= 0x10ffff) {
        value += String.fromCodePoint(point);
        i += digits;
      } else {
        // An escape YAML does not know (a Windows path typed by hand) stays as written.
        value += ESCAPES[code] ?? `\\${code}`;
      }
      continue;
    }
    value += ch;
  }
  return null;
}

/** The value of a scalar that is quoted from end to end (a trailing comment allowed); undefined if it is not. */
function wholeQuoted(text: string): string | undefined {
  if (text[0] !== '"' && text[0] !== "'") return undefined;
  const quoted = readQuoted(text, 0);
  return quoted && NOTHING_RE.test(text.slice(quoted.end)) ? quoted.value : undefined;
}

const isTagsKey = (key: string) => key.toLowerCase() === 'tags';

/** A line that opens a key: the key, and the raw text after its colon. */
function splitKeyLine(line: string): { key: string; after: string } | null {
  // Indented lines continue the key above; `#` opens a comment and `- ` a list item.
  if (/^(?:$|\s|#|-(?:\s|$))/.test(line)) return null;
  if (line[0] === '"' || line[0] === "'") {
    const quoted = readQuoted(line, 0);
    const colon = quoted && /^\s*:(?=\s|$)/.exec(line.slice(quoted.end));
    return quoted && colon ? { key: quoted.value, after: line.slice(quoted.end + colon[0].length) } : null;
  }
  // The key runs to the first colon followed by a space or the end of the
  // line, so `creación:`, `mi propiedad:` and `url: https://…` all split where
  // they should. The fallback takes the `key:value` (no space) this parser has
  // always accepted from hand-written files.
  const kv = /^(.+?)\s*:(?=\s|$)/u.exec(line) ?? /^([\p{L}\p{N}_-]+):/u.exec(line);
  return kv ? { key: kv[1]!, after: line.slice(kv[0].length) } : null;
}

/** The items of a `[a, "b, c"]` list, or null when one of them is itself a list or a map. */
function parseFlowList(inner: string): string[] | null {
  const items: string[] = [];
  let i = 0;
  while (i < inner.length) {
    const skip = inner.slice(i).search(/\S/);
    if (skip === -1) break;
    i += skip;
    let item: string;
    if (inner[i] === '"' || inner[i] === "'") {
      const quoted = readQuoted(inner, i);
      const rest = quoted && /^\s*(?:,|$)/.exec(inner.slice(quoted.end));
      if (!quoted || !rest) return null;
      item = quoted.value;
      i = quoted.end + rest[0].length;
    } else {
      const comma = inner.indexOf(',', i);
      const end = comma === -1 ? inner.length : comma;
      item = inner.slice(i, end).trim();
      if (/[[\]{}]/.test(item)) return null;
      i = end + 1;
    }
    if (item) items.push(item);
  }
  return items;
}

/**
 * A value written on its key's line (or folded onto it). Undefined means
 * "keep it verbatim": a blank value, or a structure the dialect does not model.
 */
function parseInline(key: string, text: string): string | string[] | undefined {
  if (text === '') return undefined;
  const quoted = wholeQuoted(text);
  if (quoted !== undefined) return quoted || undefined;
  if (WIKILINK_RE.test(text)) return text;
  if (text.startsWith('[') && text.endsWith(']')) {
    const items = parseFlowList(text.slice(1, -1));
    return items?.length ? items : undefined;
  }
  if (text.startsWith('{')) return undefined;

  // `tags: a, b` without brackets is common in hand-written notes, and the rest
  // of the app already treats tags as a list wherever it sees them.
  if (isTagsKey(key)) {
    return text.split(',').map((tag) => wholeQuoted(tag.trim()) ?? tag.trim()).filter(Boolean);
  }

  // Anything else is the text as written, `: ` and ` #` included: QUIPWA wrote
  // such values unquoted before it quoted them, and its notes must keep reading
  // the same.
  return text;
}

/** One `- item` of a block list; undefined when the item is itself a list, a map or a block. */
function parseItem(text: string): string | undefined {
  const quoted = wholeQuoted(text);
  if (quoted !== undefined) return quoted;
  if (WIKILINK_RE.test(text)) return text;
  if (/^(?:[[{|>]|-(?:\s|$))/.test(text) || /^.+?\s*:(?=\s|$)/u.test(text)) return undefined;
  return text;
}

/** `  - a` lines, as Obsidian's Properties write lists; undefined unless every line is a plain item at one indent. */
function parseBlockList(lines: string[]): string[] | undefined {
  const items: string[] = [];
  let indent: string | undefined;
  for (const line of lines) {
    if (NOTHING_RE.test(line)) continue;
    const m = /^(\s*)-(?:\s+(.*))?$/.exec(line);
    // Not an item (a nested map), or a line more indented than the items (an item running onto it).
    if (!m || m[1] !== (indent ??= m[1])) return undefined;
    const item = parseItem((m[2] ?? '').trim());
    if (item === undefined) return undefined;
    if (item) items.push(item);
  }
  return items.length > 0 ? items : undefined;
}

/** A scalar carried over several lines, joined as YAML folds it: a space per line break, a newline per blank line. */
function fold(first: string, lines: string[]): string | undefined {
  let text = first;
  let blanks = 0;
  for (const line of lines) {
    const part = line.trim();
    if (part === '') {
      blanks++;
      continue;
    }
    if (part.startsWith('#')) return undefined;
    text += blanks > 0 ? '\n'.repeat(blanks) : ' ';
    text += part;
    blanks = 0;
  }
  return text;
}

/** The value of one key, from the text after its colon and the lines that belong to it. */
function entryValue(key: string, after: string, lines: string[]): unknown {
  while (lines.length > 0 && NOTHING_RE.test(lines[lines.length - 1]!)) lines.pop();
  const inline = after.trim();

  let value: string | string[] | undefined;
  if (lines.length === 0) {
    value = parseInline(key, inline);
  } else if (inline === '') {
    value = parseBlockList(lines);
  } else if (!/^[|>]/.test(inline)) {
    // `|` and `>` open a block scalar, kept verbatim; anything else spilling
    // onto more lines is one value folded.
    const folded = fold(inline, lines);
    value = folded === undefined ? undefined : parseInline(key, folded);
  }
  return value ?? new RawYaml([after, ...lines].join('\n'));
}

/**
 * Parse `---\nkey: value\n---\nbody`.
 *
 * Unknown keys are preserved: notes are meant to stay portable, so an
 * Obsidian/user key must survive a parse → stringify round-trip rather than be
 * silently dropped. Scalars are read as strings, whatever YAML would make of
 * them (`5`, `true`, `2024-01-01`).
 */
export function parseFrontmatter(markdown: string): ParsedFrontmatter {
  const match = FRONTMATTER_RE.exec(markdown);
  if (!match) return { frontmatter: {}, body: markdown };

  const entries: Array<{ key: string; after: string; lines: string[] }> = [];
  let open: (typeof entries)[number] | undefined;
  for (const line of match[1]!.split(/\r?\n/)) {
    const kv = splitKeyLine(line);
    if (kv) {
      open = { ...kv, lines: [] };
      entries.push(open);
    } else if (open && (/^(?:$|\s|#)/.test(line) || (/^-(?:\s|$)/.test(line) && open.after.trim() === ''))) {
      // Indented lines, blank lines and comments belong to the key above; so do
      // `- item` lines at the margin, a list form YAML allows under an empty key.
      open.lines.push(line);
    } else {
      open = undefined; // a stray line: not YAML, and no key to keep it under
    }
  }

  const frontmatter: Record<string, unknown> = {};
  for (const { key, after, lines } of entries) frontmatter[key] = entryValue(key, after, lines);
  return { frontmatter, body: match[2]! };
}

/**
 * Look a key up ignoring case, so `Title:` and `title:` both work — files come
 * from Obsidian, other tools, and people typing by hand.
 */
export function readField(frontmatter: Record<string, unknown>, key: string): unknown {
  if (key in frontmatter) return frontmatter[key];
  const lower = key.toLowerCase();
  const found = Object.keys(frontmatter).find((k) => k.toLowerCase() === lower);
  return found ? frontmatter[found] : undefined;
}

/** Same, narrowed to a string. */
export function readString(frontmatter: Record<string, unknown>, key: string): string | undefined {
  const value = readField(frontmatter, key);
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/** Same, narrowed to a list. A bare string becomes a single-item list. */
export function readList(frontmatter: Record<string, unknown>, key: string): string[] {
  const value = readField(frontmatter, key);
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  if (typeof value === 'string' && value !== '') return [value];
  return [];
}

const isBlank = (value: unknown) =>
  value == null || value === '' || (Array.isArray(value) && value.length === 0);

/**
 * Lay the fields a screen edits over a note's original frontmatter.
 *
 * The editor only knows a handful of keys; everything else in the file
 * (Obsidian's `aliases`, `created`, a user's own keys) must survive the save.
 * Keys match ignoring case, like `readField`: an edited title replaces
 * `Title:` in place, keeping the file's spelling and key order, rather than
 * adding a second `title:` beside it. Further case-variants of an edited key
 * are dropped — left behind, `readField` could pick the stale one.
 *
 * A field left empty does not wipe a value kept verbatim (`RawYaml`): the
 * screen showed it as empty because it could not show it, so a template's
 * blank `domain:` or a list it does not model stays as the file had it.
 */
export function mergeFrontmatter(
  original: Record<string, unknown>,
  fields: Record<string, unknown>
): Record<string, unknown> {
  const edited = new Map(Object.keys(fields).map((k) => [k.toLowerCase(), k]));
  const merged: Record<string, unknown> = {};
  const written = new Set<string>();

  for (const [key, value] of Object.entries(original)) {
    const lower = key.toLowerCase();
    const field = edited.get(lower);
    if (field === undefined) {
      merged[key] = value;
    } else if (!written.has(lower)) {
      const next = fields[field];
      merged[key] = value instanceof RawYaml && isBlank(next) ? value : next;
      written.add(lower);
    }
  }
  for (const [lower, field] of edited) {
    if (!written.has(lower)) merged[field] = fields[field];
  }
  return merged;
}

/** Would this text, written bare, read back as something else — or not be YAML at all? */
function needsQuotes(text: string): boolean {
  return (
    text === '' ||
    text !== text.trim() ||
    /\p{Cc}/u.test(text) || // line breaks, tabs, control characters
    /^(?:[-?:](?:\s|$)|[,[\]{}#&*!|>'"%@`])/.test(text) || // starts like YAML syntax
    /:(?:\s|$)|\s#/.test(text) // `: ` would split it, ` #` would start a comment
  );
}

/**
 * A string as a YAML scalar: bare when it reads back as itself, double-quoted
 * (JSON's escaping is valid YAML) when not — `"Con: dos puntos"`,
 * `"[[Nota]]"`, `"#idea"`. Strings YAML would read as numbers or booleans
 * (`5`, `true`) stay bare: the parser reads every scalar as a string, so
 * Obsidian's bare `publish: true` must come back bare to stay a checkbox.
 * Pass the key for a value: a bare `tags` value with a comma would read back
 * as a list.
 */
export function yamlScalar(value: string, key?: string): string {
  const quote = needsQuotes(value) || (key !== undefined && isTagsKey(key) && value.includes(','));
  return quote ? JSON.stringify(value) : value;
}

/**
 * Serialize frontmatter + body into a full Markdown document.
 *
 * Lists are written one `- item` per line, the form Obsidian's Properties
 * write: a note Obsidian saved comes back from QUIPWA byte for byte, and
 * adding a tag changes one line of the diff instead of rewriting the whole
 * list. `RawYaml` values go back exactly as they were read.
 *
 * Empty values are dropped rather than written as blank keys — `domain:` with
 * nothing after it is noise in a file a human may open. (A blank key the file
 * itself had is a `RawYaml`, and stays.)
 */
export function stringifyFrontmatter(
  frontmatter: Record<string, unknown>,
  body: string
): string {
  const lines: string[] = [];
  for (const [key, value] of Object.entries(frontmatter)) {
    if (value instanceof RawYaml) {
      lines.push(`${yamlScalar(key)}:${value.text}`);
    } else if (Array.isArray(value)) {
      const items = value.filter((item) => item != null && item !== '').map(String);
      if (items.length === 0) continue;
      lines.push(`${yamlScalar(key)}:`, ...items.map((item) => `  - ${yamlScalar(item)}`));
    } else if (value != null && value !== '') {
      lines.push(`${yamlScalar(key)}: ${yamlScalar(String(value), key)}`);
    }
  }

  if (lines.length === 0) return body;
  return `---\n${lines.join('\n')}\n---\n${body}`;
}
